<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation } from '@tanstack/vue-query';
import { shiftsApi } from '@/api/shifts';
import type { AxiosError } from 'axios';
import { usePermissions } from '@/composables/usePermissions';
import { apiErrorText } from '@/utils/api-errors';

const { t } = useI18n();

const emit = defineEmits(['close', 'success']);

const { hasPermission } = usePermissions();

const props = defineProps({
  shiftId: {
    type: Number,
    required: true,
  },
  isMarkedShift: {
    type: Boolean,
    default: false,
  },
  isInvoiced: {
    type: Boolean,
    default: false,
  },
  closeButtonText: {
    type: String,
    default: 'Cerrar',
  },
});

const { handleSubmit } = useForm();

const { mutate, isError, isPending, isSuccess, error } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => shiftsApi.destroy(props.shiftId, undefined),
    onSuccess: () => {
      emit('success');
      emit('close');
    },
  },
);

const onSubmit = handleSubmit(() => mutate());

const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE_CONTENT = 422;
// A refused destroy says why (e.g. the shift is already in a DTE); a bare 422
// means the shift has records attached, like a 404 from a scope that hides it.
const errorText = computed(() => {
  const status = error.value?.response?.status;
  const notDeletable = t('shiftDestroyForm.notDeletableError');
  if (status === HTTP_UNPROCESSABLE_CONTENT) return apiErrorText(error.value, notDeletable);
  if (status === HTTP_NOT_FOUND) return notDeletable;

  return t('userSession.defaultError');
});
</script>
<template>
  <v-card
    v-if="props.isMarkedShift
      ? hasPermission('marked_shifts', 'destroy')
      : hasPermission('shifts', 'destroy')"
    class="w-100 pa-4"
    flat
  >
    <v-card-title>
      {{ t('shiftDestroyForm.title') }}
    </v-card-title>
    <v-form
      v-if="!isPending && !isSuccess"
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-card flat>
        <v-card-text>
          {{ t('shiftDestroyForm.confirmation') }}
        </v-card-text>
        <v-alert
          v-if="props.isInvoiced"
          class="mx-4 mb-4 rounded"
          type="warning"
          variant="tonal"
          density="compact"
        >
          {{ t('shiftDestroyForm.invoicedWarning') }}
        </v-alert>
        <div class="d-flex justify-start">
          <p class="mx-4 mb-4 text-caption">
            <v-icon
              icon="mdi-information"
              class="mb-1"
            />
            ¿Qué significa que el turno se puede borrar?
            <v-tooltip
              activator="parent"
              location="bottom"
            >
              <p>El turno no tiene:</p>
              <p v-if="!hasPermission('marked_shifts', 'destroy')">
                Su estado actualizado
              </p>
              <p>Registros escritos</p>
              <p>Registros visuales</p>
              <p>Registros de signos vitales</p>
            </v-tooltip>
          </p>
        </div>
        <v-card-actions>
          <v-btn
            :text="props.closeButtonText"
            @click="$emit('close')"
          />
          <v-spacer />
          <v-btn
            color="red"
            type="submit"
            :disabled="isPending || isSuccess"
          >
            {{ t('shiftDestroyForm.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-col
      v-if="isPending"
      class="pb-20"
    >
      <v-row class="justify-center d-flex">
        <v-progress-circular
          class="mx-auto"
          indeterminate
          color="primary"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Estamos eliminando el turno, esto puede tomar unos segundos...</p>
      </v-row>
    </v-col>
    <v-col v-if="isSuccess">
      <v-row class="justify-center d-flex">
        <v-icon
          icon="mdi-check-circle"
          color="primary"
          size="50"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Turno eliminado exitosamente!</p>
      </v-row>
    </v-col>
    <v-alert
      v-if="isError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ errorText }}
    </v-alert>
  </v-card>
</template>
